// Lightweight client-side SEO head manager.
// Updates <title>, meta tags, canonical link and JSON-LD structured data
// per "page" in this SPA, then restores the homepage defaults on cleanup.

const SITE_URL = "https://almanyakampus.com";
const JSONLD_ID = "dynamic-jsonld";
const OG_IMAGE = SITE_URL + "/og-image.png";

// Snapshot of the default (homepage) head so we can restore it.
const DEFAULTS = {
  title:
    "Almanya'da Ücretsiz Eğitim | Sıfır Almanca ile Üniversite | Almanya Kampüs",
  description:
    "Almanya'da üniversite eğitimi için ücretsiz görüşme. Sıfır Almanca ile başvuru yapın. Almanca bilmeden Almanya'da üniversite eğitimi alın. %100 ücretsiz eğitim fırsatları.",
  canonical: SITE_URL + "/",
  image: OG_IMAGE,
};

// Social scrapers (WhatsApp, Facebook, X, LinkedIn) mostly refuse SVG og:image.
// Fall back to the branded PNG so shared links always show a preview.
function normalizeOgImage(image) {
  if (!image || /\.svg(\?|$)/i.test(image)) return DEFAULTS.image;
  return image;
}

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(property, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data) {
  // Remove previous dynamic block
  const existing = document.getElementById(JSONLD_ID);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = JSONLD_ID;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Apply SEO metadata for a page.
 * @returns cleanup function that restores homepage defaults.
 */
export function applySeo({
  title,
  description,
  canonical,
  image,
  keywords,
  type = "website",
  jsonLd,
} = {}) {
  const finalTitle = title || DEFAULTS.title;
  const finalDesc = description || DEFAULTS.description;
  const finalCanonical = canonical || DEFAULTS.canonical;
  const finalImage = normalizeOgImage(image);

  document.title = finalTitle;
  setMetaByName("description", finalDesc);
  if (keywords) setMetaByName("keywords", keywords);

  setMetaByProperty("og:title", finalTitle);
  setMetaByProperty("og:description", finalDesc);
  setMetaByProperty("og:url", finalCanonical);
  setMetaByProperty("og:image", finalImage);
  setMetaByProperty("og:image:alt", finalTitle);
  setMetaByProperty("og:type", type);

  setMetaByName("twitter:title", finalTitle);
  setMetaByName("twitter:description", finalDesc);
  setMetaByName("twitter:image", finalImage);

  setCanonical(finalCanonical);
  setJsonLd(jsonLd);

  // Cleanup: restore defaults
  return () => {
    document.title = DEFAULTS.title;
    setMetaByName("description", DEFAULTS.description);
    setMetaByProperty("og:title", DEFAULTS.title);
    setMetaByProperty("og:description", DEFAULTS.description);
    setMetaByProperty("og:url", DEFAULTS.canonical);
    setMetaByProperty("og:image", DEFAULTS.image);
    setMetaByProperty("og:image:alt", DEFAULTS.title);
    setMetaByProperty("og:type", "website");
    setMetaByName("twitter:title", DEFAULTS.title);
    setMetaByName("twitter:description", DEFAULTS.description);
    setMetaByName("twitter:image", DEFAULTS.image);
    setCanonical(DEFAULTS.canonical);
    setJsonLd(null);
  };
}

export { SITE_URL };
